import DetailEventPhoto from '@components/atoms/images/detailEventPhoto';
import DetailBottomMask from '@components/atoms/mask/detailBottomMask';
import DetailContainer from '@components/atoms/container/detailContainer';
import DetailContext from '@shared/components/utils/context/detailContext';
import DetailLeftMask from '@components/atoms/mask/detailLeftMask';
import Direction from '@shared/types/enums/direction';
import Focus from '@shared/types/enums/focus';
import HorizontalAnimationContainer from '@components/atoms/container/horizontalAnimationContainer';
import OpacityAnimation from '@components/atoms/others/opacityAnimation';
import VerticalAnimationContainer from '@components/atoms/container/verticalAnimationContainer';
import useTransform from '@shared/components/utils/hooks/useTransform';
import { focusSelector } from '@shared/store/slices/focus.slice';
import { useAppSelector } from '@shared/store/hooks';
import { useContext, useEffect, useMemo, useState } from 'react';

const VERTICAL_OFFSET = 260;
const HORIZONTAL_OFFSET = 272;

interface Props {
  children?: React.ReactNode | React.ReactNode[];
}

export default function DetailBackground({ children }: Props) {
  const { asset, complete } = useContext(DetailContext);
  const { focus, stack } = useAppSelector(focusSelector);
  const { background } = useMemo(() => {
    return {
      background: asset?.background || '',
    };
  }, [asset]);

  // Animation data

  const [animation, setAnimation] = useState({
    opacity: 'detail-opacity',
    horizontal: '',
    vertical: 'detail-scroll',
  });
  const [horizontalStyle, , , changeHorizontalStyle] = useTransform(
    0,
    -HORIZONTAL_OFFSET,
    Direction.HORIZONTAL
  );
  const [verticalStyle, , , changeVerticalStyle] = useTransform(
    0,
    VERTICAL_OFFSET,
    Direction.VERTICAL
  );

  // Effect hooks

  useEffect(() => {
    let horizontalStyle = 0;
    let verticalStyle = 0;

    // Related is focused
    const relatedFocus = parseInt(`${Focus.RAILS_SUB_LIST}${0}`);
    if (
      (focus as number[])?.length &&
      (focus as number[])[0] === relatedFocus
    ) {
      verticalStyle = 1;
    }

    // Check focus
    switch (focus) {
      // Menu or modal is focused
      case Focus.LOGIN:
      case Focus.MENU:
        setAnimation((prev) => ({
          ...prev,
          horizontal: 'detail-content-in',
        }));
        horizontalStyle = 1;
        break;
      // Any other occurrence sets closing menu animation
      default:
        setAnimation((prev) => ({ ...prev, horizontal: 'detail-content-out' }));
        break;
    }

    // Ckeck stack
    if (stack.includes(Focus.MENU) && focus !== Focus.FLOATING_MENU) {
      horizontalStyle = 1;
    }
    if (stack.flat().includes(relatedFocus)) {
      verticalStyle = 1;
    }

    // Set new styles
    changeHorizontalStyle(horizontalStyle);
    changeVerticalStyle(verticalStyle);
  }, [focus]);

  return (
    <OpacityAnimation
      id='opacityAnimation'
      animation={animation.opacity}
      opacity={complete ? 1 : 0}
    >
      <DetailContainer id='detailContainer'>
        <VerticalAnimationContainer
          animation={animation.vertical}
          style={verticalStyle}
        >
          <DetailLeftMask />
          <DetailBottomMask />
          {background ? <DetailEventPhoto src={background} /> : null}
          <HorizontalAnimationContainer
            id='horizontalAnimation'
            animation={animation.horizontal}
            data-animation={animation.horizontal}
            style={horizontalStyle}
          >
            {children}
          </HorizontalAnimationContainer>
        </VerticalAnimationContainer>
      </DetailContainer>
    </OpacityAnimation>
  );
}
